import { type ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useScrollLock } from '../hooks/useScrollLock';
import { Alert, Info, Trash } from './icons';

type Tone = 'primary' | 'danger' | 'warning';
export interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  tone?: Tone;
  /** High-risk deletes: the user must type this exact text to enable the confirm button. */
  requireText?: string;
}
export interface PromptOptions {
  title: string;
  message?: ReactNode;
  label?: string;
  placeholder?: string;
  value?: string;
  confirmText?: string;
  cancelText?: string;
  multiline?: boolean;
  required?: boolean;
  validate?: (v: string) => string | null;
}
export interface ChoiceAction<T extends string> {
  label: string;
  value: T;
  tone?: 'primary' | 'danger' | '';
  cancel?: boolean;
}
export interface ChoiceOptions<T extends string> {
  title: string;
  message?: ReactNode;
  tone?: Tone;
  actions: ChoiceAction<T>[];
}

interface DialogApi {
  confirm(o: ConfirmOptions): Promise<boolean>;
  prompt(o: PromptOptions): Promise<string | null>;
  choice<T extends string>(o: ChoiceOptions<T>): Promise<T | null>;
  alert(o: { title: string; message?: ReactNode; okText?: string }): Promise<void>;
}

type Pending =
  | { kind: 'confirm'; o: ConfirmOptions; resolve: (v: boolean) => void }
  | { kind: 'prompt'; o: PromptOptions; resolve: (v: string | null) => void }
  | { kind: 'choice'; o: ChoiceOptions<string>; resolve: (v: string | null) => void };

const Ctx = createContext<DialogApi | null>(null);

export function useDialogs(): DialogApi {
  const c = useContext(Ctx);
  if (!c) throw new Error('useDialogs() must be used inside <DialogProvider>');
  return c;
}

/** Designed replacements for window.confirm/prompt/alert. One dialog at a time, queued. */
export function DialogProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<Pending[]>([]);
  const push = useCallback((p: Pending) => setQueue((q) => [...q, p]), []);
  const api = useMemo<DialogApi>(
    () => ({
      confirm: (o) => new Promise<boolean>((resolve) => push({ kind: 'confirm', o, resolve })),
      prompt: (o) => new Promise<string | null>((resolve) => push({ kind: 'prompt', o, resolve })),
      choice: <T extends string>(o: ChoiceOptions<T>) =>
        new Promise<T | null>((resolve) => push({ kind: 'choice', o: o as ChoiceOptions<string>, resolve: resolve as (v: string | null) => void })),
      alert: (o) =>
        new Promise<void>((resolve) =>
          push({ kind: 'choice', o: { title: o.title, message: o.message, actions: [{ label: o.okText ?? 'Got it', value: 'ok', tone: 'primary', cancel: true }] }, resolve: () => resolve() }),
        ),
    }),
    [push],
  );
  const current = queue[0];
  const done = useCallback(() => setQueue((q) => q.slice(1)), []);
  return (
    <Ctx.Provider value={api}>
      {children}
      {current && <DialogView key={queue.length} p={current} onDone={done} />}
    </Ctx.Provider>
  );
}

function DialogView({ p, onDone }: { p: Pending; onDone: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const [shown, setShown] = useState(false);
  const [text, setText] = useState(p.kind === 'prompt' ? (p.o.value ?? '') : '');
  const [error, setError] = useState<string | null>(null);
  useScrollLock(true);

  const tone: Tone = p.kind === 'prompt' ? 'primary' : (p.o.tone ?? 'primary');
  const finish = useCallback(
    (v: boolean | string | null) => {
      setShown(false);
      if (p.kind === 'confirm') p.resolve(v === true);
      else p.resolve(typeof v === 'string' ? v : null);
      window.setTimeout(onDone, 160);
    },
    [p, onDone],
  );
  const cancel = useCallback(() => finish(p.kind === 'confirm' ? false : null), [finish, p.kind]);

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const raf = requestAnimationFrame(() => setShown(true));
    const t = window.setTimeout(() => {
      if (field.current) { field.current.focus(); field.current.select(); }
      else box.current?.querySelector<HTMLButtonElement>('.ui-dialog-actions .ui-btn')?.focus(); // safest default: Cancel
    }, 30);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); cancel(); }
      if (e.key === 'Tab' && box.current) {
        const f = [...box.current.querySelectorAll<HTMLElement>('button, input, textarea')].filter((x) => !(x as HTMLButtonElement).disabled);
        const first = f[0];
        const last = f[f.length - 1];
        if (!first || !last) return;
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(t);
      document.removeEventListener('keydown', onKey, true);
      prev?.focus?.({ preventScroll: true });
    };
  }, [cancel]);

  const submitPrompt = () => {
    if (p.kind !== 'prompt') return;
    const v = text.trim();
    const err = !v && p.o.required !== false ? 'This field is required' : (p.o.validate?.(v) ?? null);
    setError(err);
    if (!err) finish(v);
  };

  const icon = tone === 'danger' ? <Trash /> : tone === 'warning' ? <Alert /> : <Info />;
  const requireText = p.kind === 'confirm' ? p.o.requireText : undefined;

  return createPortal(
    <div className={`ui-dialog-wrap${shown ? ' in' : ''}`} onPointerDown={(e) => e.target === e.currentTarget && cancel()}>
      <div ref={box} className={`ui-dialog ${tone}`} role="alertdialog" aria-modal="true" aria-labelledby="ui-dlg-t">
        <div className="ui-dialog-body">
          <div className="ui-dialog-icon">{icon}</div>
          <h3 id="ui-dlg-t">{p.o.title}</h3>
          {p.o.message && <p>{p.o.message}</p>}
          {requireText && (
            <div className="ui-field">
              <label className="ui-label" htmlFor="ui-dlg-req">Type <b style={{ color: 'var(--text)' }}>{requireText}</b> to confirm</label>
              <input id="ui-dlg-req" ref={field} className="ui-input" autoComplete="off" value={text} onChange={(e) => setText(e.target.value)} />
            </div>
          )}
          {p.kind === 'prompt' && (
            <div className="ui-field">
              {p.o.label && <label className="ui-label" htmlFor="ui-dlg-f">{p.o.label}</label>}
              {p.o.multiline ? (
                <textarea id="ui-dlg-f" ref={field} className="ui-textarea" placeholder={p.o.placeholder} value={text} aria-invalid={!!error} onChange={(e) => { setText(e.target.value); if (error) setError(null); }} />
              ) : (
                <input id="ui-dlg-f" ref={field} className="ui-input" placeholder={p.o.placeholder} value={text} aria-invalid={!!error} autoComplete="off"
                  onChange={(e) => { setText(e.target.value); if (error) setError(null); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitPrompt(); } }} />
              )}
              {error && <div className="ui-error" role="alert">{error}</div>}
            </div>
          )}
        </div>
        <div className="ui-dialog-actions">
          {p.kind === 'choice' ? (
            p.o.actions.map((a) => (
              <button key={a.value} type="button" className={`ui-btn ${a.tone ?? ''}`} onClick={() => finish(a.value)}>{a.label}</button>
            ))
          ) : (
            <>
              <button type="button" className="ui-btn" onClick={cancel}>{p.o.cancelText ?? 'Cancel'}</button>
              {p.kind === 'confirm' ? (
                <button type="button" className={`ui-btn ${tone === 'danger' ? 'danger' : 'primary'}`} disabled={!!requireText && text.trim() !== requireText} onClick={() => finish(true)}>
                  {p.o.confirmText ?? 'Confirm'}
                </button>
              ) : (
                <button type="button" className="ui-btn primary" onClick={submitPrompt}>{p.o.confirmText ?? 'Save'}</button>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
